import { getHubUrl, getToken } from './config';
import type { AgentState, Question, Ticket } from './types';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${getHubUrl()}${path}`;
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${getToken()}`);
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, `${res.status} ${res.statusText}: ${text || path}`);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

// Intake
export function requestUploadUrl(kind: 'audio' | 'image', ext: string) {
  return req<{ upload_url: string; r2_key: string }>('/v1/intake/upload-url', {
    method: 'POST',
    body: JSON.stringify({ kind, ext }),
  });
}

export async function uploadToR2(uploadUrl: string, blob: Blob, contentType: string): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
  });
  if (!res.ok) throw new Error(`R2 upload failed: ${res.status}`);
}

export function submitIntake(payload: {
  source: 'voice' | 'screenshot' | 'text';
  audio_r2_key?: string;
  image_r2_key?: string;
  text?: string;
}) {
  return req<{ ticket_id: string }>('/v1/intake/submit', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getTicket(id: string) {
  return req<Ticket>(`/v1/intake/${id}`);
}

export function listIntake(status?: string) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return req<{ tickets: Ticket[] }>(`/v1/intake/queue${q}`);
}

export function confirmIntake(
  id: string,
  edits?: { summary?: string; body_md?: string; priority?: string }
) {
  return req<{ ship_queue_filename: string }>(`/v1/intake/${id}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ edits: edits || {} }),
  });
}

// Questions
export function getOpenQuestions() {
  return req<{ questions: Question[] }>('/v1/questions/open');
}

export function answerQuestion(id: string, body: { chose_option?: string; text?: string }) {
  return req<{ ok: true }>(`/v1/questions/${id}/answer`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Agents
export function getAgents() {
  return req<{ agents: AgentState[] }>('/v1/agents');
}

// Push
export function getVapidPublicKey() {
  return req<{ public_key: string }>('/v1/push/vapid-public-key');
}

export function pushSubscribe(subscription: PushSubscriptionJSON) {
  return req<{ ok: true }>('/v1/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ subscription }),
  });
}

// Permissions (Phase 4)
export function decidePermission(id: string, decision: 'approve' | 'deny') {
  return req<{ ok: true }>(`/v1/permissions/${id}/decide`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
}

// Per-agent question history (graceful 404)
export async function listQuestionsForAgent(agentId: string): Promise<{ questions: Question[]; supported: boolean }> {
  try {
    const r = await req<{ questions: Question[] }>(
      `/v1/questions?agent_id=${encodeURIComponent(agentId)}&limit=50`
    );
    return { questions: r.questions || [], supported: true };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return { questions: [], supported: false };
    }
    throw e;
  }
}

// Outbound message from Steve to an agent (graceful 404 — backend pending)
export async function sendMessageToAgent(
  agentId: string,
  text: string
): Promise<{ id: string; queued_at: string; backend_pending: boolean }> {
  try {
    const r = await req<{ id: string; queued_at: string }>(
      `/v1/agents/${encodeURIComponent(agentId)}/messages`,
      { method: 'POST', body: JSON.stringify({ text }) }
    );
    return { id: r.id, queued_at: r.queued_at, backend_pending: false };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        queued_at: new Date().toISOString(),
        backend_pending: true,
      };
    }
    throw e;
  }
}
